import { Empty } from '@bufbuild/protobuf';
import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@/shared/api/api';
import { OrganizationService } from '@/shared/api/protocol-ts/api/core/svc_organization_connectweb';
import {
  CreateOrganizationReq,
  CreateOrganizationResp,
  DeleteOrganizationReq,
  DeleteOrganizationResp,
  SetOrganizationContactsReq,
  SetOrganizationContactsResp,
  SetOrganizationLanguageReq,
  SetOrganizationLanguageResp,
  SetOrganizationNameReq,
  SetOrganizationNameResp,
  SetOrganizationPrintNameReq,
  SetOrganizationPrintNameResp,
  SetOrganizationMFARequirementReq,
  SetOrganizationSettingsReq,
  SetOrganizationSettingsResp,
  SetOrganizationStaffMFARequirementReq,
  SetOrganizationTypeReq,
  SetOrganizationTypeResp,
  SetOrganizationStaffJobPositionsReq,
  SetOrganizationStaffJobPositionsResp,
  SetOrganizationStaffNameReq,
  SetOrganizationStaffNameResp,
} from '@/shared/api/protocol-ts/api/core/svc_organization_pb';
import { LocalStorageKey, SliceName } from '@/shared/config';
import { createThunkGenerator } from '@/shared/lib';

type OrganizationRequests = keyof typeof OrganizationService.methods;

const generateOrganizationThunk = createThunkGenerator<OrganizationRequests>(
  SliceName.organization,
);

export const createOrganization = generateOrganizationThunk<
  CreateOrganizationReq,
  CreateOrganizationResp
>('createOrganization', api.organization.createOrganization);

export const setOrganizationName = generateOrganizationThunk<
  SetOrganizationNameReq,
  SetOrganizationNameResp
>('setOrganizationName', api.organization.setOrganizationName);
export const setOrganizationPrintName = generateOrganizationThunk<
  SetOrganizationPrintNameReq,
  SetOrganizationPrintNameResp
>('setOrganizationPrintName', api.organization.setOrganizationPrintName);
export const setOrganizationType = generateOrganizationThunk<
  SetOrganizationTypeReq,
  SetOrganizationTypeResp
>('setOrganizationType', api.organization.setOrganizationType);
export const setOrganizationContacts = generateOrganizationThunk<
  SetOrganizationContactsReq,
  SetOrganizationContactsResp
>('setOrganizationContacts', api.organization.setOrganizationContacts);

export const setOrganizationSettings = generateOrganizationThunk<
  SetOrganizationSettingsReq,
  SetOrganizationSettingsResp
>('setOrganizationSettings', api.organization.setOrganizationSettings);

export const setOrganizationLanguage = generateOrganizationThunk<
  SetOrganizationLanguageReq,
  SetOrganizationLanguageResp
>('setOrganizationLanguage', api.organization.setOrganizationLanguage);

export const deleteOrganization = generateOrganizationThunk<
  DeleteOrganizationReq,
  DeleteOrganizationResp
>('deleteOrganization', api.organization.deleteOrganization);

export const setCurrentOrganizationByID = createAsyncThunk(
  'organization/setCurrentOrganizationID',
  async (organizationID: string) => {
    if (organizationID) {
      localStorage.setItem(LocalStorageKey.organizationID, organizationID);
    }

    return organizationID;
  },
);

export const setOrganizationStaffJobPositions = generateOrganizationThunk<
  SetOrganizationStaffJobPositionsReq,
  SetOrganizationStaffJobPositionsResp
>(
  'setOrganizationStaffJobPositions',
  api.organization.setOrganizationStaffJobPositions,
);

export const setOrganizationStaffName = generateOrganizationThunk<
  SetOrganizationStaffNameReq,
  SetOrganizationStaffNameResp
>('setOrganizationStaffName', api.organization.setOrganizationStaffName);

export const setOrganizationMFARequirement = generateOrganizationThunk<
  SetOrganizationMFARequirementReq,
  Empty
>(
  'setOrganizationMFARequirement',
  api.organization.setOrganizationMFARequirement,
);

export const setOrganizationStaffMFARequirement = generateOrganizationThunk<
  SetOrganizationStaffMFARequirementReq,
  Empty
>(
  'setOrganizationStaffMFARequirement',
  api.organization.setOrganizationStaffMFARequirement,
);
